import { barsFor } from '@/screens/chart-adapter';
import type { ViewModel } from '@/screens/generated/view-model';
import type { Ctx } from './ctx';

/** The chart bindings — the reference's `tvVals()` and `tvMarkers()`.
 *
 *  The package replaced four hand-drawn CSS candlestick charts with the licensed TradingView Advanced Charts widget,
 *  mounted through `<pf-tv-chart>`. What is left here is small on purpose: a symbol, a resolution, a theme, and a
 *  JSON string of execution markers. The chart owns the drawing; this owns *what it is a chart of*.
 *
 *  Everything crosses as a string because these are HTML attributes on a custom element. The element parses
 *  `markers` itself and re-draws when the attribute changes, which is why the marker list is serialised here rather
 *  than passed as an array.
 */

/** Timeframe chip → TradingView resolution. The chips are the trader's vocabulary, the resolutions are the widget's. */
const TF: Record<string, string> = {
  '1m': '1',
  '5m': '5',
  '15m': '15',
  '1h': '60',
  '4h': '240',
  '1d': '1D',
};

/** Which instrument each backtest book is run against. */
const BT_SYM: Record<string, string> = {
  'London Open Reversal': 'XAUUSD',
  'Trend Continuation': 'EURUSD',
  'Breakout Pullback': 'US30',
  'Asia Range Fade': 'USDJPY',
};

interface MarkerInput {
  readonly time: string;
  readonly side: string;
  readonly text: string;
}

/** Pins trades to the nearest bar on the series the chart is showing.
 *
 *  The demo trades carry a clock time (`09:42`) and no date, because they are a day's worth of activity rather than a
 *  dated history. So each one is placed at that time of day on the series' **last** day and snapped to the closest
 *  bar — a marker between two bars is a marker the widget draws in the gutter.
 *
 *  Returns `'[]'` rather than throwing when the store is absent: a chart with no markers is a chart, and an
 *  exception here would take the screen with it. */
export function tvMarkers(sym: string, res: string, list: readonly MarkerInput[]): string {
  // Through the adapter, not the store: a marker has to land on the series the chart is actually showing, and the
  // adapter is what decides which that is. Reading the store directly would pin markers to the package's synthetic
  // walk on a pane serving the server's bars — the two are different series with different last days.
  if (!sym || !list.length) return '[]';
  const bars = barsFor(sym, res);
  if (!bars.length) return '[]';

  const lastDay = Math.floor(bars[bars.length - 1]!.time / 864e5);
  const out: { time: number; price: number; side: string; text: string }[] = [];
  for (const t of list) {
    const m = /^(\d{1,2}):(\d{2})/.exec(t.time);
    // A trade with no parseable time is dropped rather than pinned somewhere plausible — a marker in the wrong place
    // is worse than a missing one, because the trader will read it.
    if (!m) continue;
    const want = lastDay * 864e5 + Number(m[1]) * 36e5 + Number(m[2]) * 6e4;
    let best = bars[0]!;
    let bestDelta = Infinity;
    for (const bar of bars) {
      const d = Math.abs(bar.time - want);
      if (d < bestDelta) {
        bestDelta = d;
        best = bar;
      }
    }
    out.push({
      time: best.time,
      price: (best as { open?: number }).open ?? 0,
      side: t.side || 'LONG',
      text: t.text,
    });
  }
  return JSON.stringify(out);
}

export function buildCharts(c: Ctx): Partial<ViewModel> {
  const S = c.S;

  // End of Day reviews today's session — chart the instrument it actually traded most, rather than a fixed default
  // that may not appear in the day at all.
  const today = c.trades().filter((r) => r[1] === 0);
  const tally = new Map<string, number>();
  for (const r of today) tally.set(String(r[3]), (tally.get(String(r[3])) ?? 0) + 1);
  const eodSym = [...tally.entries()].sort(([, a], [, b]) => b - a)[0]?.[0] ?? 'XAUUSD';
  const eodRows = today.filter((r) => String(r[3]) === eodSym);

  return {
    // ── TradeHub: the live desk ────────────────────────────────────────────────────────────────────────────────
    // The instrument the order desk is working. One selection drives the chart, the ticket's prices and the order,
    // so the three cannot disagree about what is being traded.
    tvSym: S.deskSym || 'XAUUSD',
    tvInterval: TF[S.tf] ?? '60',
    tvTheme: S.theme === 'light' ? 'light' : 'dark',

    // ── Backtest ──────────────────────────────────────────────────────────────────────────────────────────────
    btSym: BT_SYM[S.btStrat] ?? 'XAUUSD',
    btInterval: '60',
    // The simulated backtest sessions carry no timestamps, so there is nothing honest to pin to a bar. Empty, rather
    // than markers placed where they would look right.
    btMarkers: '[]',

    // ── Replay ────────────────────────────────────────────────────────────────────────────────────────────────
    rpInterval: '60',

    // ── End of Day ────────────────────────────────────────────────────────────────────────────────────────────
    eodSym,
    eodMarkers: tvMarkers(
      eodSym,
      '15',
      eodRows.map((r) => ({
        time: String(r[12]),
        side: String(r[4]),
        text: `${String(r[3])} ${Number(r[8]) >= 0 ? '+' : '−'}$${Math.abs(Number(r[8])).toFixed(0)}`,
      })),
    ),
    eodPriceNote: eodRows.length
      ? `${eodSym} · M15 — today’s session with ${eodRows.length}${eodRows.length === 1 ? ' entry marked' : ' entries marked'}`
      : `${eodSym} · M15 — today’s session`,
  };
}
